import { escapeReferenceToken } from '@criteria/json-pointer'
import {
  BreadcrumbItem,
  BreadcrumbLabel,
  BreadcrumbList,
  BreadcrumbSeparator,
  Button,
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Heading,
  Input,
  Label,
  Textarea
} from '@criteria-labs/react'
import { Service } from '@criteria-labs/docs-kit'
import { XMarkIcon } from '@heroicons/react/24/solid'
import { useState } from 'react'
import { Controller, FormProvider, SubmitHandler, useForm, useFormContext } from 'react-hook-form'
import { PageFormBody, PageFormFooter } from '../PageForm'
import { InspectorArticle, InspectorBody, InspectorHeader } from '../article'
import { AddSecurityScheme, SecurityScheme, SecuritySchemeRef } from '../changes/security-scheme'
import { useRouter } from '../router'
import { toUniqueProposedName } from '../toUniqueProposedName'
import { useDispatchChange } from '../useDispatchChange'
import { messsageFromError } from '../util/messageFromError'

type Inputs = {
  securitySchemeName: string
  scheme: string
  bearerFormat?: string
  description?: string
}

export function NewHTTPAuth({ service }: { service: Service }) {
  const { push } = useRouter()

  const defaultIdentifier = toUniqueProposedName(
    'HTTPAuth',
    Object.keys(service.openAPI.components?.securitySchemes ?? {})
  )

  const dispatchChange = useDispatchChange()
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, ...formState },
    setError,
    ...methods
  } = useForm<Inputs>({
    defaultValues: {
      securitySchemeName: defaultIdentifier,
      scheme: 'bearer'
    }
  })

  const watchedScheme = watch('scheme')

  const onSubmit: SubmitHandler<Inputs> = ({ securitySchemeName, scheme, bearerFormat, description }) => {
    try {
      const ref: SecuritySchemeRef = `#/components/securitySchemes/${escapeReferenceToken(securitySchemeName)}`
      const value: SecurityScheme = {
        type: 'http',
        scheme,
        bearerFormat:
          scheme.toLowerCase() === 'bearer' && bearerFormat && bearerFormat.length > 0 ? bearerFormat : undefined,
        description: description && description.length > 0 ? description : undefined
      }
      dispatchChange(new AddSecurityScheme(ref, value))
      push('/')
    } catch (error) {
      setError('root', { message: messsageFromError(error) ?? 'Could not create security scheme' })
    }
  }

  return (
    <InspectorArticle>
      <InspectorHeader className="space-y-3">
        <div className="flex items-center justify-between">
          <BreadcrumbList className="min-h-10">
            <BreadcrumbItem onClick={() => push('/')}>
              <BreadcrumbLabel>API</BreadcrumbLabel>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLabel>New HTTP Authentication Configuration</BreadcrumbLabel>
            </BreadcrumbItem>
          </BreadcrumbList>
          <Button aria-label="Close" plain onClick={() => push('/')}>
            <XMarkIcon />
          </Button>
        </div>
        <Heading>Create a new HTTP Authentication Configuration</Heading>
      </InspectorHeader>
      <InspectorBody>
        <FormProvider {...{ register, handleSubmit, formState: { errors, ...formState }, setError, watch, ...methods }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {errors.root && <ErrorMessage className="mt-3">{errors.root.message}</ErrorMessage>}
            <PageFormBody className="space-y-8">
              <Fieldset>
                <FieldGroup>
                  <Field>
                    <Label>Security scheme name</Label>
                    <Input {...register('securitySchemeName', { required: true })} />
                    <Description>
                      A key that will identify the security scheme object in the OpenAPI document.
                    </Description>
                  </Field>
                  <Field>
                    <Label>HTTP authentication scheme</Label>
                    <SchemeCombobox />
                  </Field>
                  <Field>
                    <Label>Bearer format</Label>
                    <Input {...register('bearerFormat')} disabled={watchedScheme.toLowerCase() !== 'bearer'} />
                  </Field>
                  <Field>
                    <Label>Description</Label>
                    <Textarea {...register('description')} />
                  </Field>
                </FieldGroup>
              </Fieldset>
            </PageFormBody>
            <PageFormFooter>
              <Button type="submit" color="indigo">
                Create HTTP Authentication Configuration
              </Button>
            </PageFormFooter>
          </form>
        </FormProvider>
      </InspectorBody>
    </InspectorArticle>
  )
}

const httpSchemes: Record<string, string> = {
  basic: 'Basic',
  bearer: 'Bearer',
  digest: 'Digest'
}

function SchemeCombobox() {
  const [search, setSearch] = useState('')
  const matchingValues =
    search === ''
      ? Object.keys(httpSchemes)
      : Object.keys(httpSchemes).filter((value) => value.toLowerCase().includes(search.toLowerCase()))
  const { control } = useFormContext()
  return (
    <Controller
      control={control}
      name="scheme"
      render={({ field, fieldState, formState }) => (
        <Combobox value={field.value} onChange={(value) => field.onChange(value)} disabled={field.disabled}>
          <ComboboxInput onChange={(event) => setSearch(event.target.value)} onBlur={() => field.onBlur()} />
          <ComboboxOptions>
            {search.length > 0 && matchingValues.length === 0 && (
              <ComboboxOption value={search}>{search}</ComboboxOption>
            )}
            {matchingValues.map((matchingValue) => (
              <ComboboxOption key={matchingValue} value={matchingValue}>
                {httpSchemes[matchingValue]}
              </ComboboxOption>
            ))}
          </ComboboxOptions>
        </Combobox>
      )}
    ></Controller>
  )
}
